import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Send,
  Plus,
  MessageCircle,
  Trash2,
  ChevronDown,
  ChevronUp,
  Loader2,
  Sparkles,
  Palette,
  Lightbulb,
  Music,
  User,
} from 'lucide-react';
import { Button, ConfirmModal } from '../../components/ui';
import { AppLayout } from '../../components/AppLayout';
import { useAuth } from '../../context';
import {
  coachService,
  ConversationDto,
  ConversationDetailDto,
  MessageDto,
} from '../../services';
import './Coach.css';

// Format relative time
const formatRelativeTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// Format message time (HH:MM)
const formatMessageTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Starter prompts for empty state
const starterPrompts = [
  { icon: Palette, text: "I'm feeling creatively stuck" },
  { icon: Lightbulb, text: 'Help me brainstorm ideas' },
  { icon: Music, text: 'How do I stay motivated?' },
  { icon: Sparkles, text: 'Review my creative process' },
];

export const Coach: React.FC = () => {
  useAuth();

  // Conversations state
  const [conversations, setConversations] = useState<ConversationDto[]>([]);
  const [activeConversation, setActiveConversation] = useState<ConversationDetailDto | null>(null);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const [deleteConversationId, setDeleteConversationId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Refs
  const chatEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Scroll to bottom of chat
  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  // Fetch conversations list
  const fetchConversations = useCallback(async () => {
    try {
      setIsLoadingConversations(true);
      setError(null);
      const data = await coachService.getConversations();
      setConversations(data);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
      setError('Failed to load conversations. Please try again.');
    } finally {
      setIsLoadingConversations(false);
    }
  }, []);

  // Load conversations on mount
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Load a specific conversation
  const loadConversation = useCallback(async (id: string) => {
    try {
      setIsLoadingMessages(true);
      setError(null);
      const data = await coachService.getConversation(id);
      if (data) {
        setActiveConversation(data);
        setSidebarOpen(false);
        scrollToBottom();
      }
    } catch (err) {
      console.error('Failed to load conversation:', err);
      setError('Failed to load conversation. Please try again.');
    } finally {
      setIsLoadingMessages(false);
    }
  }, [scrollToBottom]);

  // Start a new conversation
  const startNewConversation = useCallback(() => {
    setActiveConversation(null);
    setMessageInput('');
    setSidebarOpen(false);
    setError(null);
  }, []);

  // Send a message
  const handleSendMessage = useCallback(async (content?: string) => {
    const messageContent = content || messageInput.trim();
    if (!messageContent || isSending) return;

    setIsSending(true);
    setError(null);
    setMessageInput('');

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    try {
      const response = await coachService.sendMessage({
        content: messageContent,
        conversationId: activeConversation?.id,
      });

      setActiveConversation(response);
      scrollToBottom();

      // Update conversations list
      setConversations(prev => {
        const existing = prev.find(c => c.id === response.id);
        if (existing) {
          return prev.map(c =>
            c.id === response.id
              ? {
                  ...c,
                  title: response.title,
                  lastMessageAt: response.lastMessageAt,
                  messageCount: response.messageCount,
                }
              : c
          );
        }
        // New conversation - add to top
        return [
          {
            id: response.id,
            userId: response.userId,
            title: response.title,
            startedAt: response.startedAt,
            lastMessageAt: response.lastMessageAt,
            messageCount: response.messageCount,
            isActive: response.isActive,
            createdAt: response.createdAt,
          },
          ...prev,
        ];
      });
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to send message. Please try again.');
      // Restore the message so the user doesn't lose it
      setMessageInput(messageContent);
    } finally {
      setIsSending(false);
    }
  }, [messageInput, isSending, activeConversation, scrollToBottom]);

  // Delete a conversation
  const handleDeleteConversation = useCallback(async () => {
    if (!deleteConversationId) return;

    setIsDeleting(true);
    try {
      await coachService.deleteConversation(deleteConversationId);
      setConversations(prev => prev.filter(c => c.id !== deleteConversationId));
      if (activeConversation?.id === deleteConversationId) {
        setActiveConversation(null);
      }
      setDeleteConversationId(null);
    } catch (err) {
      console.error('Failed to delete conversation:', err);
      setError('Failed to delete conversation. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  }, [deleteConversationId, activeConversation]);

  // Handle textarea auto-resize
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }, []);

  // Handle Enter key to send (Shift+Enter for newline)
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  // Handle starter prompt click
  const handleStarterPrompt = useCallback((text: string) => {
    handleSendMessage(text);
  }, [handleSendMessage]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (activeConversation?.messages?.length) {
      scrollToBottom();
    }
  }, [activeConversation?.messages?.length, scrollToBottom]);

  const messages = activeConversation?.messages || [];
  const hasConversations = conversations.length > 0;
  const showEmptyState = !activeConversation && !isLoadingMessages;

  return (
    <AppLayout pageTitle="Creative Coach">
      <div className="coach">
        {/* Ambient background */}
        <div className="coach__ambient">
          <div className="coach__ambient-shape coach__ambient-shape--1" />
          <div className="coach__ambient-shape coach__ambient-shape--2" />
        </div>

        {/* Main Content */}
        <div className="coach__main">
          {/* Sidebar Toggle */}
          {hasConversations && (
            <button
              className="coach__sidebar-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <span className="coach__sidebar-toggle-left">
                <MessageCircle size={16} />
                {activeConversation
                  ? activeConversation.title
                  : `${conversations.length} conversation${conversations.length !== 1 ? 's' : ''}`}
              </span>
              {sidebarOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          )}

          {/* Conversation Sidebar */}
          <div className={`coach__sidebar ${sidebarOpen ? 'coach__sidebar--open' : ''}`}>
            <div className="coach__sidebar-header">
              <h3 className="coach__sidebar-title">Conversations</h3>
              <button
                className="coach__new-chat-btn"
                onClick={startNewConversation}
              >
                <Plus size={14} />
                New
              </button>
            </div>
            <div className="coach__conversation-list">
              {conversations.map(conversation => (
                <button
                  key={conversation.id}
                  className={`coach__conversation-item ${
                    activeConversation?.id === conversation.id
                      ? 'coach__conversation-item--active'
                      : ''
                  }`}
                  onClick={() => loadConversation(conversation.id)}
                >
                  <span className="coach__conversation-icon">
                    <MessageCircle size={16} />
                  </span>
                  <span className="coach__conversation-info">
                    <span className="coach__conversation-title">
                      {conversation.title}
                    </span>
                    <span className="coach__conversation-meta">
                      {formatRelativeTime(conversation.lastMessageAt)}
                      {' \u00B7 '}
                      {conversation.messageCount} message{conversation.messageCount !== 1 ? 's' : ''}
                    </span>
                  </span>
                  <button
                    className="coach__conversation-delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConversationId(conversation.id);
                    }}
                    aria-label="Delete conversation"
                  >
                    <Trash2 size={14} />
                  </button>
                </button>
              ))}
            </div>
          </div>

          {/* Chat Area */}
          {isLoadingConversations ? (
            <div className="coach__loading">
              <Loader2 size={32} className="coach__loading-spinner" />
              <p>Loading conversations...</p>
            </div>
          ) : isLoadingMessages ? (
            <div className="coach__loading">
              <Loader2 size={32} className="coach__loading-spinner" />
              <p>Loading messages...</p>
            </div>
          ) : showEmptyState ? (
            <div className="coach__empty">
              <div className="coach__empty-icon">
                <Sparkles size={36} />
              </div>
              <h3>Your Creative Coach</h3>
              <p>
                Ask anything about your creative journey.
                Get personalized advice, overcome blocks, and stay inspired.
              </p>
              <div className="coach__suggestions">
                {starterPrompts.map((prompt, index) => (
                  <button
                    key={index}
                    className="coach__suggestion"
                    onClick={() => handleStarterPrompt(prompt.text)}
                    disabled={isSending}
                  >
                    <prompt.icon size={18} />
                    {prompt.text}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="coach__chat">
              {activeConversation && (
                <div className="coach__chat-intro">
                  <p>{activeConversation.title}</p>
                </div>
              )}

              {messages.map((message: MessageDto, index: number) => (
                <div
                  key={message.id || index}
                  className={`coach__message coach__message--${message.role === 'User' ? 'user' : 'coach'}`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div
                    className={`coach__message-avatar coach__message-avatar--${
                      message.role === 'User' ? 'user' : 'coach'
                    }`}
                  >
                    {message.role === 'User' ? (
                      <User size={16} />
                    ) : (
                      <Sparkles size={16} />
                    )}
                  </div>
                  <div>
                    <div className="coach__message-bubble">
                      {message.content}
                    </div>
                    <div className="coach__message-time">
                      {formatMessageTime(message.createdAt)}
                    </div>
                  </div>
                </div>
              ))}

              {isSending && (
                <div className="coach__typing">
                  <div className="coach__message-avatar coach__message-avatar--coach">
                    <Sparkles size={16} />
                  </div>
                  <div className="coach__typing-bubble">
                    <div className="coach__typing-dots">
                      <span className="coach__typing-dot" />
                      <span className="coach__typing-dot" />
                      <span className="coach__typing-dot" />
                    </div>
                    <span className="coach__typing-text">Thinking...</span>
                  </div>
                </div>
              )}

              <div ref={chatEndRef} />
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="coach__error">
              <p>{error}</p>
              <Button variant="ghost" onClick={() => setError(null)}>
                Dismiss
              </Button>
            </div>
          )}

          {/* Message Input */}
          <div className="coach__input-area">
            <form
              className="coach__input-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
            >
              <div className="coach__input-wrapper">
                <textarea
                  ref={textareaRef}
                  className="coach__input"
                  placeholder="Ask your creative coach..."
                  value={messageInput}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  disabled={isSending}
                />
              </div>
              <button
                type="submit"
                className={`coach__send-btn ${isSending ? 'coach__send-btn--loading' : ''}`}
                disabled={!messageInput.trim() || isSending}
                aria-label="Send message"
              >
                {isSending ? <Loader2 size={20} /> : <Send size={20} />}
              </button>
            </form>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={!!deleteConversationId}
          onClose={() => setDeleteConversationId(null)}
          onConfirm={handleDeleteConversation}
          title="Delete Conversation"
          message="Are you sure you want to delete this conversation? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
          isLoading={isDeleting}
        />
      </div>
    </AppLayout>
  );
};

export default Coach;
